import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Microphone, MicrophoneSlash } from "@phosphor-icons/react";

/**
 * AudioViolationWatcher
 * Client-side microphone monitor that runs alongside camera-based proctoring during
 * an active exam attempt. Requests mic permission on mount, uses the Web Audio API's
 * AnalyserNode to sample RMS volume in short windows, and posts a proctor violation
 * whenever sustained loud sound is detected for more than the trigger threshold.
 *
 * We DO NOT stream or record raw audio — only a numeric level is analysed in-browser.
 *
 * Props:
 *  - attemptId (string, required)
 *  - onPermissionDenied? callback fired when the user blocks mic access
 *
 * Visual: a compact chip in the top-right showing live level meter + status:
 *   green = OK · amber = warming/loud · red = permission denied or sustained loud
 */
const RMS_LOUD_THRESHOLD = 0.14;      // RMS above which we consider the room "loud"
const CONSECUTIVE_LOUD_MS = 4000;     // continuous loud sound before a violation fires
const COOLDOWN_MS = 30000;            // minimum gap between violations to avoid spam
const SAMPLE_MS = 250;                // analyser sampling cadence

export default function AudioViolationWatcher({ attemptId, onPermissionDenied }) {
  const [status, setStatus] = useState("initializing"); // initializing | listening | denied | loud | error
  const [level, setLevel] = useState(0);
  const [violationCount, setViolationCount] = useState(0);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const loudSinceRef = useRef(null);
  const lastViolationAtRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    let intervalId = null;

    async function init() {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          setStatus("error");
          return;
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: false },
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        const AC = window.AudioContext || window.webkitAudioContext;
        const ctx = new AC();
        audioCtxRef.current = ctx;
        const src = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        analyser.smoothingTimeConstant = 0.4;
        src.connect(analyser);
        const buf = new Float32Array(analyser.fftSize);
        setStatus("listening");

        const tick = () => {
          try {
            analyser.getFloatTimeDomainData(buf);
            let sum = 0;
            for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
            const rms = Math.sqrt(sum / buf.length);
            setLevel(rms);
            const now = Date.now();
            if (rms >= RMS_LOUD_THRESHOLD) {
              if (loudSinceRef.current == null) loudSinceRef.current = now;
              const loudFor = now - loudSinceRef.current;
              setStatus(loudFor > 1000 ? "loud" : "listening");
              if (loudFor >= CONSECUTIVE_LOUD_MS && now - lastViolationAtRef.current > COOLDOWN_MS) {
                lastViolationAtRef.current = now;
                loudSinceRef.current = now; // reset so we don't fire again immediately
                fireViolation(rms, loudFor);
              }
            } else {
              loudSinceRef.current = null;
              setStatus((s) => (s === "loud" ? "listening" : s));
            }
          } catch { /* analyser errors are transient */ }
        };
        intervalId = setInterval(tick, SAMPLE_MS);
      } catch (e) {
        const denied = e?.name === "NotAllowedError" || e?.name === "PermissionDeniedError";
        setStatus(denied ? "denied" : "error");
        if (denied) {
          toast.error("Microphone permission was blocked. Proctoring cannot monitor audio.");
          onPermissionDenied?.();
        } else {
          toast.error(`Microphone init failed: ${e?.message || e}`);
        }
      }
    }

    async function fireViolation(rms, durationMs) {
      setViolationCount((n) => n + 1);
      try {
        await api.post("/attempts/violations", {
          attempt_id: attemptId,
          type: "audio_loud",
          severity: "medium",
          details: `Sustained loud audio detected: rms=${rms.toFixed(3)} for ~${Math.round(durationMs / 1000)}s`,
        });
      } catch { /* silently — proctor still sees camera signal */ }
    }

    init();

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
      try { audioCtxRef.current?.close(); } catch { /* noop */ }
    };
  }, [attemptId, onPermissionDenied]);

  const meterPct = Math.min(100, Math.round(level * 400));  // scale for visibility
  const tone = status === "denied" || status === "error"
    ? "bg-red-50 border-red-300 text-red-800"
    : status === "loud"
    ? "bg-amber-50 border-amber-400 text-amber-900"
    : "bg-emerald-50 border-emerald-300 text-emerald-900";

  return (
    <div
      className={`inline-flex items-center gap-2 border px-2.5 py-1.5 ${tone}`}
      data-testid="audio-watcher"
      data-status={status}
      title={status === "denied" ? "Mic blocked — proctor will be notified" : status === "loud" ? "Loud sound detected" : "Audio monitoring active"}
    >
      {status === "denied" || status === "error" ? (
        <MicrophoneSlash size={14} weight="fill" />
      ) : (
        <Microphone size={14} weight="fill" />
      )}
      <div className="w-14 h-1.5 bg-white/70 border border-current/20 overflow-hidden">
        <div className="h-full bg-current transition-[width] duration-100" style={{ width: `${meterPct}%` }} />
      </div>
      <span className="text-[10px] uppercase tracking-wider font-semibold" data-testid="audio-watcher-status">
        {status === "listening" ? "MIC OK" : status === "loud" ? "LOUD" : status === "denied" ? "MIC OFF" : status === "initializing" ? "…" : "ERR"}
      </span>
      {violationCount > 0 && (
        <span className="text-[10px] font-mono bg-white/60 px-1" data-testid="audio-watcher-violations">{violationCount}</span>
      )}
    </div>
  );
}
